import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { pagesAPI } from '../services/api';
import InquiryForm from '../components/inquiries/InquiryForm';
import SEO from '../components/seo/SEO';

/**
 * Lightweight markdown-ish renderer:
 * - Blank line separates paragraphs
 * - Lines starting with "- " become bullet items
 * - **bold** and *italic* / _italic_ inline marks
 * Stays simple & XSS-safe (no raw HTML).
 */
const inline = (text) => {
  const parts = [];
  let rest = text;
  let key = 0;
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/;
  while (rest) {
    const m = rest.match(regex);
    if (!m) { parts.push(rest); break; }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      parts.push(<strong key={`b-${key++}`}>{tok.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`i-${key++}`}>{tok.slice(1, -1)}</em>);
    }
    rest = rest.slice(m.index + tok.length);
  }
  return parts;
};

const renderBody = (body) => {
  if (!body) return null;
  const blocks = body.split(/\n\s*\n/);
  return blocks.map((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim().length);
    const isList = lines.every((l) => l.trim().startsWith('- '));
    if (isList) {
      return (
        <ul key={`ul-${bi}`} className="list-disc pl-6 space-y-1 text-gray-700">
          {lines.map((l, li) => (
            <li key={`li-${bi}-${li}`}>{inline(l.trim().slice(2))}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={`p-${bi}`} className="text-gray-700 leading-relaxed">
        {lines.map((l, li) => (
          <React.Fragment key={`ln-${bi}-${li}`}>
            {li > 0 && <br />}{inline(l)}
          </React.Fragment>
        ))}
      </p>
    );
  });
};

const StaticPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    pagesAPI.get(slug)
      .then((res) => setPage(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <Layout>
      <SEO
        title={page?.title}
        description={page?.body ? page.body.substring(0, 180).replace(/\s+/g, ' ').trim() : undefined}
      />
      <div className="bg-gray-50 min-h-screen py-10" data-testid={`page-${slug}`}>
        <div className="max-w-3xl mx-auto px-4">
          {loading ? (
            <div className="text-center text-gray-500 py-16">Yükleniyor…</div>
          ) : error || !page ? (
            <div className="text-center text-gray-500 py-16">Sayfa yüklenemedi.</div>
          ) : (
            <article className="bg-white rounded-lg shadow-sm p-8 md:p-10">
              <header className="border-b border-gray-100 pb-5 mb-6">
                <div className="w-12 h-1 bg-red-600 rounded-full mb-4" />
                <h1
                  className="text-3xl md:text-4xl font-black text-gray-900 leading-tight tracking-tight"
                  data-testid={`page-title-${slug}`}
                >
                  {page.title}
                </h1>
              </header>

              <div className="prose-content space-y-5" data-testid={`page-body-${slug}`}>
                {renderBody(page.body)}
              </div>

              {(page.contact_email || page.contact_phone || page.contact_address) && (
                <div className="mt-10 pt-6 border-t border-gray-100">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">İletişim</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {page.contact_email && (
                      <a
                        href={`mailto:${page.contact_email}`}
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors"
                        data-testid="page-contact-email-link"
                      >
                        <Mail className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs uppercase font-bold text-gray-500 tracking-wider">E-posta</p>
                          <p className="text-sm text-gray-900 break-words">{page.contact_email}</p>
                        </div>
                      </a>
                    )}
                    {page.contact_phone && (
                      <a
                        href={`tel:${page.contact_phone.replace(/\s+/g, '')}`}
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-red-50 transition-colors"
                        data-testid="page-contact-phone-link"
                      >
                        <Phone className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs uppercase font-bold text-gray-500 tracking-wider">Telefon</p>
                          <p className="text-sm text-gray-900">{page.contact_phone}</p>
                        </div>
                      </a>
                    )}
                    {page.contact_address && (
                      <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-testid="page-contact-address">
                        <MapPin className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs uppercase font-bold text-gray-500 tracking-wider">Adres</p>
                          <p className="text-sm text-gray-900">{page.contact_address}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          )}

          {/* Inquiry form — only on Reklam ve İş Birliği page */}
          {!loading && !error && slug === 'reklam-isbirligi' && (
            <div className="mt-8">
              <InquiryForm />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StaticPage;
